import * as Sentry from '@sentry/react';
import { fetch as crossFetch } from '@vizit/auth';

// Use globalThis.fetch in tests (so MSW can intercept) and crossFetch in production.
// import.meta.env.VITEST is set by Vitest at compile time — no runtime timing issues.
const fetch = import.meta.env.VITEST ? globalThis.fetch : crossFetch;

interface S3UploadFields {
  key: string;
  AWSAccessKeyId: string;
  policy: string;
  signature: string;
  'x-amz-security-token': string;
}

const uploadToS3 = async (
  id: string,
  url: string,
  file: File,
  uploadFields: S3UploadFields,
  onSuccess: () => Promise<void>,
  onError: (error?: Error) => void,
) => {
  const formData = new FormData();

  // Add all upload fields to formData
  for (const [key, value] of Object.entries(uploadFields)) {
    formData.append(key, value);
  }

  // Add the file last
  formData.append('file', file);

  try {
    Sentry.addBreadcrumb({
      message: 'Uploading file to S3.',
      level: 'info',
      data: { url, fileSize: file.size, fileType: file.type, asset_id: id },
    });

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      Sentry.addBreadcrumb({
        message: `S3 upload request was not successful - ${response.status} ${response.statusText}`,
        level: 'error',
        data: { status: response.status, statusText: response.statusText, asset_id: id },
      });

      throw new Error(`${id} S3 upload request was not successful - ${response.status} ${response.statusText}`);
    }

    Sentry.addBreadcrumb({
      message: 'S3 upload successful.',
      level: 'info',
      data: { asset_id: id },
    });

    await onSuccess();
  } catch (error) {
    // Note: The actual error capturing should be done in the onError callback where more context is available
    onError(error as Error);
  }
};

export { uploadToS3 };
export type { S3UploadFields };
